import PageHeader from './PageHeader';
import '../styles/UserSettings.css';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import ApiService from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import i18n from '../i18n/config';
import LanguageSwitcher from './LanguageSwitcher';
import SummaryUpdateSettings from './SummaryUpdateSettings';
import PraktijkdataUserSettings from './PraktijkdataUserSettings';
import EduVUserSettings from './EduVUserSettings';
import { logger } from '../utils/logger';
import type { UserPreferences } from '../types';

/**
 * Formatteert een Nederlands telefoonnummer naar E.164 formaat (+316XXXXXXXX)
 */
const formatPhoneNumber = (phoneNumber: string): string => {
    if (!phoneNumber || phoneNumber.trim() === '') {
        return '';
    }

    // Verwijder alle spaties en streepjes
    let cleanNumber = phoneNumber.replace(/[\s-]/g, '');

    // Converteer verschillende formaten naar E.164
    if (cleanNumber.startsWith('06')) {
        cleanNumber = `+31${cleanNumber.substring(1)}`;
    } else if (cleanNumber.startsWith('00316')) {
        cleanNumber = `+31${cleanNumber.substring(4)}`;
    } else if (cleanNumber.startsWith('0031')) {
        cleanNumber = `+31${cleanNumber.substring(4)}`;
    }

    return cleanNumber;
};

/**
 * Valideert een Nederlands mobiel nummer in E.164 formaat
 */
const validatePhoneNumber = (phoneNumber: string): { valid: boolean; error?: string } => {
    if (!phoneNumber || phoneNumber.trim() === '') {
        return { valid: true }; // Leeg is toegestaan
    }

    const formatted = formatPhoneNumber(phoneNumber);

    if (!formatted.startsWith('+')) {
        return {
            valid: false,
            error: i18n.t('userSettings.validation.invalidPhoneFormat')
        };
    }

    // Validatie voor Nederlands mobiel nummer
    const mobilePattern = /^\+316[1-9]\d{7}$/;
    if (!mobilePattern.test(formatted)) {
        return {
            valid: false,
            error: i18n.t('userSettings.validation.invalidDutchMobile')
        };
    }

    return { valid: true };
};

/** Niet-negatief geheel getal of leeg; anders ongeldig. */
const parseRetentionDaysInput = (raw: string): { valid: true; value: number | null } | { valid: false } => {
    const trimmed = raw.trim();
    if (trimmed === '') return { valid: true, value: null };
    if (!/^\d+$/.test(trimmed)) return { valid: false };
    return { valid: true, value: parseInt(trimmed, 10) };
};

const numberOrEmpty = (value: number | null | undefined): string =>
    value !== null && value !== undefined ? String(value) : '';

const minNullable = (a: number | null, b: number | null): number | null =>
    a === null ? b : b === null ? a : Math.min(a, b);

/**
 * Verwachte termijnen tijdens het invullen, volgens de resolutieregels van de
 * backend (leeg verslagveld volgt de opnametermijn; de kortste van eigen en
 * organisatietermijn wint). Alleen voor de live conclusie en de bevestiging;
 * na opslaan is het antwoord van de backend leidend.
 */
const predictRetention = (input: {
    recordings: number | null;
    summaries: number | null;
    orgRecordings: number | null;
    orgSummaries: number | null;
}): { recordings: number | null; summaries: number | null } => ({
    recordings: minNullable(input.recordings, input.orgRecordings),
    summaries: minNullable(input.summaries ?? input.recordings, input.orgSummaries ?? input.orgRecordings),
});

interface ConfirmDialogState {
    title: string;
    message: string;
    confirmLabel: string;
    onConfirm: () => void | Promise<void>;
}

const UserSettings: React.FC = () => {
    const { t } = useTranslation();
    const apiService = new ApiService();
    const { getDomain, checkDomainStatus, user } = useAuth();
    const [serverDomain, setServerDomain] = useState('');
    const [isServerOnline, setIsServerOnline] = useState<boolean | null>(null);
    const [showServerDetails, setShowServerDetails] = useState(false);
    const [activeSettingsTab, setActiveSettingsTab] = useState('general');
    const settingsTabs = ['general', 'user', 'app', 'developer'];

    // Form state
    const [personalContext, setPersonalContext] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [authMethod, setAuthMethod] = useState<'email' | 'sms'>('email');
    const [retentionDaysStr, setRetentionDaysStr] = useState<string>('');
    const [organizationRetentionDays, setOrganizationRetentionDays] = useState<number | null>(null);
    const [effectiveRetentionDays, setEffectiveRetentionDays] = useState<number | null>(null);
    // Verslagretentie: null = volgt de opnametermijn; wist alleen de verslagen, niet de opname
    const [summaryRetentionDaysStr, setSummaryRetentionDaysStr] = useState<string>('');
    const [organizationSummaryRetentionDays, setOrganizationSummaryRetentionDays] = useState<number | null>(null);
    const [effectiveSummaryRetentionDays, setEffectiveSummaryRetentionDays] = useState<number | null>(null);
    // Laatst opgeslagen eigen waarden, om te weten of het formulier afwijkt van de server
    const [savedRetention, setSavedRetention] = useState<{ recordings: string; summaries: string }>({ recordings: '', summaries: '' });

    // UI state
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [summaryRetentionError, setSummaryRetentionError] = useState('');
    const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);

    /** Verwerkt de retentievelden uit een GET/POST-antwoord in het formulier. */
    const applyRetentionPrefs = (prefs: UserPreferences) => {
        setRetentionDaysStr(numberOrEmpty(prefs.retentionDays));
        setOrganizationRetentionDays(prefs.organizationRetentionDays ?? null);
        setEffectiveRetentionDays(prefs.effectiveRetentionDays ?? null);
        setSummaryRetentionDaysStr(numberOrEmpty(prefs.summaryRetentionDays));
        setOrganizationSummaryRetentionDays(prefs.organizationSummaryRetentionDays ?? null);
        setEffectiveSummaryRetentionDays(prefs.effectiveSummaryRetentionDays ?? null);
        setSavedRetention({
            recordings: numberOrEmpty(prefs.retentionDays),
            summaries: numberOrEmpty(prefs.summaryRetentionDays),
        });
    };

    // Live conclusie: zolang het formulier afwijkt van wat is opgeslagen, tonen we
    // de verwachte termijnen; anders de effectieve waarden uit de backend.
    const recordingsInput = parseRetentionDaysInput(retentionDaysStr);
    const summaryInput = parseRetentionDaysInput(summaryRetentionDaysStr);
    const predicted = predictRetention({
        recordings: recordingsInput.valid ? recordingsInput.value : null,
        summaries: summaryInput.valid ? summaryInput.value : null,
        orgRecordings: organizationRetentionDays,
        orgSummaries: organizationSummaryRetentionDays,
    });
    const retentionDirty =
        retentionDaysStr.trim() !== savedRetention.recordings ||
        summaryRetentionDaysStr.trim() !== savedRetention.summaries;
    const shownRecordingDays = retentionDirty ? predicted.recordings : effectiveRetentionDays;
    const shownSummaryDays = retentionDirty ? predicted.summaries : effectiveSummaryRetentionDays;

    const recordingsOutcome = (days: number | null): string =>
        days === null
            ? t('userSettings.retention.none')
            : t('userSettings.retention.effective', { days });
    const summaryOutcome = (days: number | null): string =>
        days === null
            ? t('userSettings.retention.summary.none')
            : days === 0
                ? t('userSettings.retention.summary.effectiveZero')
                : t('userSettings.retention.summary.effective', { days });
    const withPreview = (outcome: string): string =>
        retentionDirty ? t('userSettings.retention.preview', { outcome }) : outcome;

    // Load preferences on mount
    useEffect(() => {
        setServerDomain(getDomain());
        checkDomainStatus().then(setIsServerOnline).catch(() => setIsServerOnline(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const loadPreferences = async () => {
            try {
                setIsLoading(true);
                const prefs = await apiService.getUserPreferences();
                setPersonalContext(prefs.personalContext || '');
                setPhoneNumber(prefs.phoneNumber || '');
                setAuthMethod(prefs.authMethod || 'email');
                applyRetentionPrefs(prefs);
            } catch (err) {
                logger.error('Error loading preferences:', err);
                setError(t('userSettings.messages.loadError'));
            } finally {
                setIsLoading(false);
            }
        };

        loadPreferences();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setPhoneNumber(value);
        setPhoneError('');
        setError('');
        setSuccess('');

        // Live validation
        if (value) {
            const validation = validatePhoneNumber(value);
            if (!validation.valid) {
                setPhoneError(validation.error || '');
            }
        }
    };

    const handleAuthMethodChange = (method: 'email' | 'sms') => {
        setAuthMethod(method);
        // Telefoonnummer is alleen relevant voor SMS; wis de foutmelding bij e-mail
        if (method === 'email') {
            setPhoneError('');
        }
        setError('');
        setSuccess('');
    };

    const handleSave = async () => {
        setError('');
        setSuccess('');

        // Validate phone if SMS is selected or if phone is filled
        if (phoneNumber) {
            const validation = validatePhoneNumber(phoneNumber);
            if (!validation.valid) {
                setPhoneError(validation.error || '');
                return;
            }
        }

        // If SMS is selected, phone is required
        if (authMethod === 'sms' && !phoneNumber) {
            setPhoneError(t('userSettings.validation.phoneRequiredForSms'));
            return;
        }

        const summaryRetention = parseRetentionDaysInput(summaryRetentionDaysStr);
        if (!summaryRetention.valid) {
            setSummaryRetentionError(t('userSettings.retention.summary.validation'));
            return;
        }
        const summaryRetentionDaysValue = summaryRetention.value;

        // Verslagen worden definitief verwijderd (opname en transcript blijven tot
        // de opnametermijn). Vraag bevestiging als de verwachte termijn korter is
        // dan wat nu effectief geldt (geen termijn telt als oneindig) of als hij 0
        // is (verwijderen bij de eerstvolgende run). Een leeg verslagveld volgt de
        // opnametermijn, dus ook wissen kan verkorten.
        const predictedSummaryDays = predicted.summaries;
        const shortensSummaryRetention =
            predictedSummaryDays !== null &&
            (predictedSummaryDays === 0 ||
                effectiveSummaryRetentionDays === null ||
                predictedSummaryDays < effectiveSummaryRetentionDays);

        if (shortensSummaryRetention) {
            setConfirmDialog({
                title: t('userSettings.retention.summary.confirmTitle'),
                message: [
                    predictedSummaryDays === 0
                        ? t('userSettings.retention.summary.confirmIntroZero')
                        : t('userSettings.retention.summary.confirmIntro', { days: predictedSummaryDays }),
                    t('userSettings.retention.summary.confirmOutcome', {
                        summaries: summaryOutcome(predicted.summaries),
                        recordings: recordingsOutcome(predicted.recordings),
                    }),
                    t('userSettings.retention.summary.confirmFooter'),
                ].join('\n\n'),
                confirmLabel: t('userSettings.retention.summary.confirmButton'),
                onConfirm: () => performSave(summaryRetentionDaysValue),
            });
            return;
        }

        await performSave(summaryRetentionDaysValue);
    };

    const performSave = async (summaryRetentionDaysValue: number | null) => {
        setIsSaving(true);

        try {
            const formattedPhone = formatPhoneNumber(phoneNumber);
            const retentionDaysValue = retentionDaysStr.trim() === '' ? null : parseInt(retentionDaysStr, 10);

            const updatedPrefs = await apiService.updateUserPreferences({
                personalContext,
                phoneNumber: formattedPhone,
                authMethod,
                retentionDays: retentionDaysValue,
                summaryRetentionDays: summaryRetentionDaysValue,
            });

            // Effectieve termijnen komen uit het antwoord; niet zelf uitrekenen
            applyRetentionPrefs(updatedPrefs);

            setSuccess(t('userSettings.messages.saveSuccess'));
        } catch (err) {
            logger.error('Error saving preferences:', err);
            // 422 over de verslagtermijn: melding bij het veld, niets is opgeslagen
            if (axios.isAxiosError(err) && err.response?.status === 422) {
                const backendMessage: unknown = err.response.data?.error ?? err.response.data?.message;
                if (typeof backendMessage === 'string' && backendMessage.includes('summary_retention_days')) {
                    setSummaryRetentionError(backendMessage);
                    return;
                }
            }
            setError(t('userSettings.messages.saveError'));
        } finally {
            setIsSaving(false);
        }
    };

    const organizationSummaryLimit = organizationSummaryRetentionDays ?? organizationRetentionDays;

    if (isLoading) {
        return (
            <div className="flex min-h-full items-center justify-center py-32">
                <div className="flex items-center space-x-2">
                    <svg className="animate-spin h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-lg font-medium">{t('common.loading')}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-full pb-20">
            <PageHeader title={t('userSettings.title')} />
            <div className="mx-auto max-w-6xl px-5 py-9 sm:px-8 lg:px-10">
                {/* Page header */}
                <p className="mb-6 text-sm" style={{ color: 'var(--text-muted)' }}>{t('userSettings.subtitle')}</p>

                <div className="settings-layout">
                <nav className="settings-tabs" role="tablist" aria-label={t('userSettings.title')}>
                    {settingsTabs.map((tab, index) => <button key={tab} type="button" role="tab" id={`settings-tab-${tab}`}
                        aria-selected={activeSettingsTab === tab} aria-controls={`settings-panel-${tab}`}
                        tabIndex={activeSettingsTab === tab ? 0 : -1} onClick={() => setActiveSettingsTab(tab)}
                        onKeyDown={event => {
                            let next = index;
                            if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = (index + 1) % settingsTabs.length;
                            else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = (index + settingsTabs.length - 1) % settingsTabs.length;
                            else if (event.key === 'Home') next = 0;
                            else if (event.key === 'End') next = settingsTabs.length - 1;
                            else return;
                            event.preventDefault(); setActiveSettingsTab(settingsTabs[next]);
                            document.getElementById(`settings-tab-${settingsTabs[next]}`)?.focus();
                        }}>{t(`userSettings.tabs.${tab}`)}</button>)}
                </nav>
                {/* Settings card */}
                <div className="animate-fade-in rounded-2xl border border-primary-100 bg-container p-6 shadow-soft sm:p-8">
                    {/* Error message */}
                    {error && (
                        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                            <p className="text-sm text-red-600">{error}</p>
                        </div>
                    )}

                    {/* Success message */}
                    {success && (
                        <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-3">
                            <p className="text-sm text-green-600">{success}</p>
                        </div>
                    )}

                    <section role="tabpanel" id="settings-panel-general" aria-labelledby="settings-tab-general" hidden={activeSettingsTab !== 'general'}>
                    {/* Server sectie */}
                    <div className="mb-6">
                        <div className="flex items-center mb-4">
                            <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
                            </svg>
                            <h2 className="text-lg font-semibold text-gray-900">{t('userSettings.server.heading')}</h2>
                        </div>
                        <button
                            type="button"
                            onClick={() => setShowServerDetails((prev) => !prev)}
                            aria-expanded={showServerDetails}
                            className="flex w-full items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-left transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            <span className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${isServerOnline === false ? 'bg-red-500' : 'bg-green-500'}`} />
                            <span className="flex-1 text-sm font-medium text-gray-700">
                                {isServerOnline === false
                                    ? t('userSettings.server.disconnected')
                                    : t('userSettings.server.connected')}
                            </span>
                            <svg
                                className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${showServerDetails ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {showServerDetails && (
                            <div className="mt-2 space-y-3 rounded-md border border-gray-200 bg-white px-3 py-2.5">
                                <div>
                                    <p className="text-xs text-gray-500">{t('userSettings.server.environmentLabel')}</p>
                                    <p className="mt-0.5 break-all text-sm text-gray-700">{serverDomain}</p>
                                </div>
                                {user && (
                                    <div>
                                        <p className="text-xs text-gray-500">{t('userSettings.server.loggedInAsLabel')}</p>
                                        <p className="mt-0.5 break-all text-sm text-gray-700">{user.email || user.username}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Taal sectie */}
                    <div className="mb-6">
                        <div className="flex items-center mb-4">
                            <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.6 9h16.8M3.6 15h16.8M12 3a15 15 0 010 18M12 3a15 15 0 000 18" />
                            </svg>
                            <h2 className="text-lg font-semibold text-gray-900">{t('userSettings.language.heading')}</h2>
                        </div>
                        <div className="flex items-center gap-2">
                            <LanguageSwitcher tone="light" />
                            <span className="group relative inline-flex">
                                <button
                                    type="button"
                                    aria-label={t('userSettings.language.hint')}
                                    className="inline-flex h-5 w-5 items-center justify-center rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </button>
                                <span
                                    role="tooltip"
                                    className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 w-64 -translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-xs leading-relaxed text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
                                >
                                    {t('userSettings.language.hint')}
                                </span>
                            </span>
                        </div>
                    </div>

                    </section>
                    <section role="tabpanel" id="settings-panel-app" aria-labelledby="settings-tab-app" hidden={activeSettingsTab !== 'app'}>
                        <h2 className="mb-3 text-lg font-semibold">{t('userSettings.tabs.app')}</h2>
                        <p className="mb-6 text-sm">{t('userSettings.applicationHint')}</p>
                    </section>
                    <section role="tabpanel" id="settings-panel-developer" aria-labelledby="settings-tab-developer" hidden={activeSettingsTab !== 'developer'}>
                        <SummaryUpdateSettings />
                    </section>
                    <section role="tabpanel" id="settings-panel-user" aria-labelledby="settings-tab-user" hidden={activeSettingsTab !== 'user'}>

                    {/* Profiel sectie */}
                    <div className="mb-6">
                        <div className="flex items-center mb-4">
                            <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <h2 className="text-lg font-semibold text-gray-900">{t('userSettings.profile.heading')}</h2>
                        </div>

                        <div>
                            <label htmlFor="personalContext" className="block text-sm font-medium text-gray-700 mb-2">
                                {t('userSettings.profile.personalContextLabel')}
                            </label>
                            <textarea
                                id="personalContext"
                                value={personalContext}
                                onChange={(e) => {
                                    setPersonalContext(e.target.value);
                                    setError('');
                                    setSuccess('');
                                }}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-600 focus:border-primary-600"
                                placeholder={t('userSettings.profile.personalContextPlaceholder')}
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                {t('userSettings.profile.personalContextHint')}
                            </p>
                        </div>
                    </div>

                    {/* Authenticatie sectie */}
                    <div className="mb-6">
                        <div className="flex items-center mb-4">
                            <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                            <h2 className="text-lg font-semibold text-gray-900">{t('userSettings.authentication.heading')}</h2>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('userSettings.authentication.methodLabel')}
                                </label>
                                <div className="flex rounded-md shadow-sm">
                                    <button
                                        type="button"
                                        onClick={() => handleAuthMethodChange('email')}
                                        className={`flex-1 px-4 py-2 text-sm font-medium rounded-l-md border ${authMethod === 'email'
                                            ? 'bg-primary-600 text-white border-primary-600'
                                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        {t('userSettings.authentication.methodEmail')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleAuthMethodChange('sms')}
                                        className={`flex-1 px-4 py-2 text-sm font-medium rounded-r-md border-t border-b border-r ${authMethod === 'sms'
                                            ? 'bg-primary-600 text-white border-primary-600'
                                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        {t('userSettings.authentication.methodSms')}
                                    </button>
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                    {t('userSettings.authentication.methodHint')}
                                </p>
                            </div>

                            {authMethod === 'sms' && (
                                <div>
                                    <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('userSettings.authentication.phoneLabel')}
                                    </label>
                                    <input
                                        id="phoneNumber"
                                        type="tel"
                                        value={phoneNumber}
                                        onChange={handlePhoneChange}
                                        className={`w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-600 focus:border-primary-600 ${phoneError ? 'border-red-300' : 'border-gray-300'
                                            }`}
                                        placeholder={t('userSettings.authentication.phonePlaceholder')}
                                    />
                                    {phoneError && (
                                        <p className="mt-1 text-xs text-red-600">{phoneError}</p>
                                    )}
                                    <p className="mt-1 text-xs text-gray-500">
                                        {t('userSettings.authentication.phoneHint')}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Praktijkdata-koppeling (alleen zichtbaar als er een
                        Praktijkdata-exportconfiguratie bekend is; slaat direct
                        op, los van de opslaan-knop hieronder) */}
                    <PraktijkdataUserSettings />

                    {/* Medewerker-koppeling voor het onderwijssysteem; zelfde
                        opzet: verbergt zichzelf als er geen Edu-V-configuratie
                        bekend is en slaat direct op */}
                    <EduVUserSettings />

                    {/* Data Retentie sectie */}
                    <div className="mb-6">
                        <div className="flex items-center mb-4">
                            <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <h2 className="text-lg font-semibold text-gray-900">{t('userSettings.retention.heading')}</h2>
                        </div>

                        <div>
                            <label htmlFor="retentionDaysStr" className="block text-sm font-medium text-gray-700 mb-2">
                                {t('userSettings.retention.label')}
                            </label>
                            <input
                                id="retentionDaysStr"
                                type="number"
                                min="1"
                                value={retentionDaysStr}
                                onChange={(e) => {
                                    setRetentionDaysStr(e.target.value);
                                    setError('');
                                    setSuccess('');
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-600 focus:border-primary-600"
                                placeholder={t('userSettings.retention.placeholder')}
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                {t('userSettings.retention.hint')}
                            </p>
                            <p className="mt-2 text-sm font-medium text-gray-700" aria-live="polite">
                                {withPreview(recordingsOutcome(shownRecordingDays))}
                            </p>
                        </div>

                        {/* Verslagen: eigen termijn, null volgt de opnametermijn */}
                        <div className="mt-5">
                            <label htmlFor="summaryRetentionDaysStr" className="block text-sm font-medium text-gray-700 mb-2">
                                {t('userSettings.retention.summary.label')}
                            </label>
                            <input
                                id="summaryRetentionDaysStr"
                                type="number"
                                min="0"
                                step="1"
                                inputMode="numeric"
                                value={summaryRetentionDaysStr}
                                onChange={(e) => {
                                    setSummaryRetentionDaysStr(e.target.value);
                                    setSummaryRetentionError('');
                                    setError('');
                                    setSuccess('');
                                }}
                                aria-invalid={!!summaryRetentionError}
                                aria-describedby="summaryRetentionDaysHint"
                                className={`w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-600 focus:border-primary-600 ${summaryRetentionError ? 'border-red-300' : 'border-gray-300'}`}
                                placeholder={t('userSettings.retention.summary.placeholder')}
                            />
                            {summaryRetentionError && (
                                <p className="mt-1 text-xs text-red-600" role="alert">{summaryRetentionError}</p>
                            )}
                            <p id="summaryRetentionDaysHint" className="mt-1 text-xs text-gray-500">
                                {t('userSettings.retention.summary.hint')}
                            </p>
                            {organizationSummaryLimit !== null && (
                                <p className="mt-1 text-xs text-gray-500">
                                    {t('userSettings.retention.summary.organizationLimit', { days: organizationSummaryLimit })}
                                </p>
                            )}
                            <p className="mt-2 text-sm font-medium text-gray-700" aria-live="polite">
                                {withPreview(summaryOutcome(shownSummaryDays))}
                            </p>
                        </div>
                    </div>

                    </section>
                    {/* Save button */}
                    <button
                        hidden={activeSettingsTab !== 'user'}
                        onClick={handleSave}
                        disabled={isSaving || !!phoneError || !!summaryRetentionError}
                        className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSaving ? (
                            <div className="flex items-center justify-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {t('common.saving')}
                            </div>
                        ) : (
                            t('common.save')
                        )}
                    </button>
                </div>
            </div>

            </div>

            {/* Bevestiging bij verkorten van de verslagtermijn (verslagen verdwijnen definitief) */}
            {confirmDialog && (
                <div
                    className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in"
                    onClick={() => setConfirmDialog(null)}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="retention-confirm-title"
                        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 id="retention-confirm-title" className="text-lg font-semibold text-gray-900 mb-2">{confirmDialog.title}</h3>
                        <p className="text-sm text-gray-600 mb-6 whitespace-pre-line">{confirmDialog.message}</p>
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                autoFocus
                                onClick={() => setConfirmDialog(null)}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type="button"
                                onClick={async () => {
                                    const cb = confirmDialog.onConfirm;
                                    setConfirmDialog(null);
                                    try {
                                        await cb();
                                    } catch (e) {
                                        logger.error('Confirm action failed:', e);
                                    }
                                }}
                                className="px-4 py-2 rounded-lg text-sm text-white bg-red-600 hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                                {confirmDialog.confirmLabel}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserSettings;
